// Direct-to-bucket uploads. `uploadFetch` is a drop-in for `fetch` on the old base64 endpoints
// (/auth/uploadImage and /auth/bulkUploadImages): it asks the API for a short-lived signed URL, PUTs the
// (resized) image straight to the bucket and answers with the same JSON the old endpoint returned.
// If anything fails before the upload finishes it falls back to the old endpoint, so nothing breaks.
//
// Manual feed/reel media (`uploadManualContent`) uses the same idea with an admin-only staging upload.

const MAX_EDGE = 1600;
const QUALITY = 0.82;
const MIN_BYTES = 500 * 1024;
const MIN_EDGE_TO_RESIZE = 2000;
const BULK_CONCURRENCY = 3;

const isBulk = (url) => /\/auth\/bulkUploadImages$/.test(url);
const isSingle = (url) => /\/auth\/uploadImage$/.test(url);

const jsonResponse = (data) => ({ ok: true, status: 200, json: async () => data });

const toBlob = (image) => {
  const dataUrl = /^data:([^;]+);base64,/.exec(image);
  const b64 = dataUrl ? image.slice(dataUrl[0].length) : image;
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  let type = dataUrl ? dataUrl[1] : "";
  if (!type) {
    if (bytes[0] === 0x89 && bytes[1] === 0x50) type = "image/png";
    else if (bytes[0] === 0x47 && bytes[1] === 0x49) type = "image/gif";
    else if (bytes[0] === 0x52 && bytes[1] === 0x49) type = "image/webp";
    else type = "image/jpeg";
  }
  return new Blob([bytes], { type });
};

// Same standard as the server used: long edge <= 1600 px, EXIF rotation applied, metadata stripped.
// Only photos over 500 KB or 2000 px are touched, and the original is kept if the result is not smaller.
export const shrinkImageBlob = async (blob) => {
  if (!["image/jpeg", "image/png", "image/webp"].includes(blob.type)) return blob;
  try {
    const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
    const longEdge = Math.max(bitmap.width, bitmap.height);
    if (blob.size <= MIN_BYTES && longEdge <= MIN_EDGE_TO_RESIZE) {
      bitmap.close();
      return blob;
    }
    const scale = Math.min(1, MAX_EDGE / longEdge);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const out = await new Promise((resolve) => canvas.toBlob(resolve, blob.type, QUALITY));
    return out && out.size < blob.size ? out : blob;
  } catch (e) {
    return blob;
  }
};

const apiBase = (url) => url.replace(/\/auth\/(uploadImage|bulkUploadImages)$/, "");

const authHeaders = (init) => {
  const h = (init && init.headers) || {};
  const out = {};
  Object.keys(h).forEach((k) => {
    if (k.toLowerCase() === "authorization") out.Authorization = h[k];
  });
  return out;
};

const uploadOne = async (base, init, image, name) => {
  const blob = await shrinkImageBlob(toBlob(image));
  const signedRes = await fetch(`${base}/auth/upload-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(init) },
    body: JSON.stringify({ name, contentType: blob.type, size: blob.size }),
  });
  if (!signedRes.ok) throw new Error(`upload-url ${signedRes.status}`);
  const signed = await signedRes.json();
  const put = await fetch(signed.uploadUrl, { method: "PUT", headers: signed.headers, body: blob });
  if (!put.ok) throw new Error(`bucket PUT ${put.status}`);
  return { url: signed.fileUrl, format: blob.type };
};

const runLimited = async (items, worker) => {
  const results = new Array(items.length);
  let next = 0;
  const lane = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      results[i] = await worker(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(BULK_CONCURRENCY, items.length) }, lane));
  return results;
};

export const uploadFetch = async (url, init) => {
  if (!(isSingle(url) || isBulk(url)) || !init || typeof init.body !== "string") return fetch(url, init);
  try {
    const base = apiBase(url);
    const body = JSON.parse(init.body);
    if (isSingle(url)) {
      if (!body.image || !body.name) return fetch(url, init);
      return jsonResponse(await uploadOne(base, init, body.image, body.name));
    }
    const images = body.images;
    if (!Array.isArray(images) || images.length === 0 || images.some((i) => !i.image || !i.description)) {
      return fetch(url, init);
    }
    const results = await runLimited(images, async (item, index) => {
      try {
        const r = await uploadOne(base, init, item.image, item.description);
        return { success: true, url: r.url, format: r.format, originalDescription: item.description, index };
      } catch (e) {
        if (e && /^(upload-url|bucket PUT)/.test(e.message)) throw e; // let the fallback below redo everything
        return { success: false, error: e.message, originalDescription: item.description, index };
      }
    });
    const successful = results.filter((r) => r.success).length;
    return jsonResponse({ total: images.length, successful, failed: images.length - successful, results });
  } catch (e) {
    // Signed upload not available (older API, bucket CORS, network): use the old endpoint
    return fetch(url, init);
  }
};

// Manual Reel/Feed upload: file -> staging/ in the bucket -> API converts it from there.
// Returns the same JSON as POST /admin/content/manual. Falls back to that endpoint if staging fails.
export const uploadManualContent = async (apiBaseUrl, token, { file, type, category, creatorTag, customCaption }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const legacy = async () => {
    const body = new FormData();
    body.append("file", file);
    body.append("type", type);
    body.append("category", category);
    body.append("creatorTag", creatorTag);
    body.append("customCaption", customCaption);
    const response = await fetch(`${apiBaseUrl}/admin/content/manual`, { method: "POST", headers, body });
    return response.json();
  };
  let stagingKey;
  try {
    const kind = file.type === "video/mp4" ? "video" : "image";
    const signedRes = await fetch(`${apiBaseUrl}/admin/content/upload-url`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ kind }),
    });
    if (!signedRes.ok) throw new Error(`upload-url ${signedRes.status}`);
    const signed = await signedRes.json();
    const put = await fetch(signed.uploadUrl, { method: "PUT", headers: signed.headers, body: file });
    if (!put.ok) throw new Error(`bucket PUT ${put.status}`);
    stagingKey = signed.stagingKey;
  } catch (e) {
    return legacy();
  }
  const response = await fetch(`${apiBaseUrl}/admin/content/manual-direct`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ stagingKey, type, category, creatorTag, customCaption }),
  });
  return response.json();
};
